import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CloseableAndFilterableInsight } from '../../closeable-filterable-insight.class';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MatTableDataSource,
} from '@angular/material/table';
import { NgClass } from '@angular/common';
import { StormInfo, toStormInfo } from '../../../../shared/models/storm.model';
import { ReportsService } from '../../../../data/reports/reports.service';
import { DateFilter } from '../../../../shared/models/DateFilter';
import { ToastService } from '../../../../shared/services/toast.service';
import { FilterButtonComponent } from '../../../../shared/components/buttons/filter-button/filter-button.component';
import { FilterBarComponent } from '../../../../shared/components/filter-bar/filter-bar.component';
import { MatDialog } from '@angular/material/dialog';
import { AddStormDialogComponent } from './add-storm-dialog/add-storm-dialog.component';
import { InfiniteScroll } from '../../../../shared/tools/InfiniteScrollTools';
import { StormReportTableComponent } from './storm-report-table/storm-report-table.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-storm-report',
  imports: [
    MatIconButton,
    MatTooltip,
    MatIcon,
    FilterButtonComponent,
    FilterBarComponent,
    NgClass,
    StormReportTableComponent,
  ],
  templateUrl: './storm-report.component.html',
  styleUrl: './storm-report.component.scss',
})
export class StormReportComponent extends CloseableAndFilterableInsight implements AfterViewInit {

  @ViewChild(StormReportTableComponent) reportTableComponent: StormReportTableComponent;
  dataSource: MatTableDataSource<StormInfo> = new MatTableDataSource([]);

  pageSize = 50;
  pageIndex = 0;
  totalElements = 0;

  isLoading = false;
  isScrollLoading = false;
  dateFilter: DateFilter = undefined;

  constructor(
    protected readonly router: Router,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly reportService: ReportsService,
    protected readonly dialog: MatDialog,
    protected readonly toast: ToastService
  ) {
    super(router, activatedRoute);
  }

  ngAfterViewInit() {
    this.loadReport();
  }

  toggleLoading() {
    setTimeout(() => (this.isLoading = !this.isLoading));
  }

  private filtersInitialized() {
    return this.dateFilter !== undefined;
  }

  dateFilterChanged(dateFilter: DateFilter) {
    this.dateFilter = dateFilter;
    this.loadReport();
  }

  loadReport() {
    if (this.reportTableComponent && this.filtersInitialized()) {
      this.toggleLoading();
      this.pageIndex = 0;
      this.isScrollLoading = false;
      this.reportService
        .getStormReport(
          !!this.dateFilter.from ? this.dateFilter.from : null,
          !!this.dateFilter.to ? this.dateFilter.to : null,
          this.pageIndex,
          this.pageSize,
          this.reportTableComponent.getSort(),
        )
        .then(response => {
          // console.log(`initialized, load count ${response.data.content.length} of ${response.data.totalElements}`);
          this.dataSource.data = response.content.map(r => toStormInfo(r));
          this.totalElements = response.totalElements;
          this.pageSize = response.pageable.pageSize;
          this.toggleLoading();
        })
        .catch(error => {
          this.toggleLoading();
          this.toast.longer('Error while fetching initial data from the server');
          console.error(error);
        });
    }
  }

  onScroll(e) {
    InfiniteScroll.onScroll(e, () => this.extendReport());
  }

  extendReport() {
    if (!this.isScrollLoading && this.dataSource.data.length < this.totalElements) {
      this.pageIndex++;
      this.isScrollLoading = true;
      this.reportService
        .getStormReport(
          !!this.dateFilter.from ? this.dateFilter.from : null,
          !!this.dateFilter.to ? this.dateFilter.to : null,
          this.pageIndex,
          this.pageSize,
          this.reportTableComponent.getSort(),
        )
        .then(response => {
          const extendedData = [...this.dataSource.data];
          extendedData.push(...response.content.map(r => toStormInfo(r)));
          // console.log(`extended, load count ${extendedData.length} of ${this.totalElements}`);
          this.dataSource.data = extendedData;

          this.isScrollLoading = false;
        })
        .catch(error => {
          this.toast.longer('Error while fetching more data from the server');
          console.error(error);
          this.isScrollLoading = false;
        });
    }
  }

  addStorm() {
    const dialogRef = this.dialog.open(AddStormDialogComponent, {
      width: '480px',
      data: {},
    });

    firstValueFrom(dialogRef.afterClosed()).then(value => {
      if (value) {
        this.loadReport();
      }
    });
  }
}
